import React from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { QualityBadge } from './QualityBadge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { cn } from '../lib/utils';
import type {
  ABRAlgorithm,
  ABRStats,
  BandwidthDataPoint,
  HLSLevel,
  QualitySwitchEvent,
} from '../types';

const MAX_BUFFER_DISPLAY = 35;

function formatBandwidth(kbps: number): string {
  if (kbps <= 0) return '-';
  if (kbps >= 1000) return `${(kbps / 1000).toFixed(1)} Mbps`;
  return `${kbps} kbps`;
}

function formatLatency(ms: number): string {
  return ms <= 0 ? '-' : `${ms} ms`;
}

function formatTime(ts: number): string {
  return new Date(ts).toTimeString().slice(0, 8);
}

interface MetricCardProps {
  label: string;
  value: React.ReactNode;
  sub?: string;
  alert?: boolean;
  highlight?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({ label, value, sub, alert, highlight }) => (
  <div className={cn(
    'space-y-2 border-2 border-foreground bg-card p-3 shadow-neo-sm',
    alert && 'bg-destructive/10',
    highlight && 'bg-accent/15'
  )}>
    <p className="text-[10px] font-mono font-black uppercase tracking-widest text-foreground/70">
      {label}
    </p>
    <div className={cn(
      'font-mono text-base font-black leading-none',
      alert ? 'text-destructive' : 'text-foreground'
    )}>
      {value}
    </div>
    {sub && <p className="text-[11px] font-bold text-foreground/65">{sub}</p>}
  </div>
);

const ABR_ALGO_INFO: Record<ABRAlgorithm, { short: string; description: string }> = {
  ewma: {
    short: 'EWMA',
    description: 'Throughput-based estimate for selecting the next bitrate.',
  },
  bola: {
    short: 'BOLA',
    description: 'Buffer-based selection using current buffer health.',
  },
};

const TOOLTIP_STYLE: React.CSSProperties = {
  background: 'hsl(var(--card))',
  border: '2px solid hsl(var(--border))',
  borderRadius: 0,
  color: 'hsl(var(--foreground))',
  fontFamily: 'JetBrains Mono, Consolas, monospace',
  fontSize: 11,
  boxShadow: '4px 4px 0 hsl(var(--shadow))',
};

interface ABRMonitorProps {
  stats: ABRStats;
  switchLog: QualitySwitchEvent[];
  bandwidthHistory: BandwidthDataPoint[];
  levels: HLSLevel[];
  currentLevel: number;
  abrAlgorithm: ABRAlgorithm;
  onAlgorithmChange: (algo: ABRAlgorithm) => void;
}

export const ABRMonitor: React.FC<ABRMonitorProps> = ({
  stats,
  switchLog,
  bandwidthHistory,
  levels,
  currentLevel,
  abrAlgorithm,
  onAlgorithmChange,
}) => {
  const getLevelLabel = (idx: number) =>
    idx < 0 || idx >= levels.length ? 'auto' : `${levels[idx].height}p`;

  return (
    <div className="overflow-hidden border-2 border-foreground bg-card font-mono shadow-neo-sm sm:shadow-neo">
      <div className="flex flex-col gap-3 border-b-2 border-foreground px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
        <div className="flex items-center gap-2">
          <span className="relative flex h-3 w-3">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-60" />
            <span className="relative inline-flex h-3 w-3 rounded-full border-2 border-foreground bg-accent" />
          </span>
          <span className="text-xs font-black uppercase tracking-widest text-foreground">
            ABR Monitor
          </span>
          <span className="border-2 border-foreground bg-background px-2 py-0.5 text-[10px] font-black uppercase text-foreground">
            Live
          </span>
        </div>
        <span className="text-[11px] font-black uppercase tracking-widest text-foreground/75">
          Level: {getLevelLabel(currentLevel)}
        </span>
      </div>

      <div className="flex flex-col gap-3 border-b-2 border-foreground px-3 py-3 lg:flex-row lg:items-center lg:px-4">
        <span className="text-[10px] font-black uppercase tracking-widest text-foreground/70">
          Algorithm
        </span>
        <div className="flex gap-2">
          {(Object.keys(ABR_ALGO_INFO) as ABRAlgorithm[]).map((algo) => {
            const active = abrAlgorithm === algo;
            return (
              <button
                key={algo}
                type="button"
                onClick={() => onAlgorithmChange(algo)}
                title={ABR_ALGO_INFO[algo].description}
                className={cn(
                  'border-2 border-foreground px-3 py-1 text-[11px] font-black uppercase shadow-neo-sm transition',
                  active
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-background text-foreground hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-neo'
                )}
              >
                {ABR_ALGO_INFO[algo].short}
              </button>
            );
          })}
        </div>
        <span className="text-xs font-bold text-foreground/70 lg:ml-auto">
          {ABR_ALGO_INFO[abrAlgorithm].description}
        </span>
      </div>

      <div className="space-y-4 p-3 sm:p-4">
        <div className="grid grid-cols-1 gap-3 min-[380px]:grid-cols-2 xl:grid-cols-3">
          <div className="space-y-2 border-2 border-foreground bg-card p-3 shadow-neo-sm">
            <p className="text-[10px] font-mono font-black uppercase tracking-widest text-foreground/70">
              Quality
            </p>
            <QualityBadge quality={stats.currentQuality} />
          </div>

          <MetricCard
            label="Bandwidth"
            value={formatBandwidth(stats.bandwidth)}
            sub="EWMA estimate"
            highlight
          />
          <MetricCard
            label="Level bitrate"
            value={formatBandwidth(stats.instantBitrate)}
            sub="From manifest"
          />
          <MetricCard
            label="Buffer"
            value={`${stats.bufferLength.toFixed(1)} s`}
            sub={`${stats.bufferFillPercent}% full`}
            alert={stats.bufferLength < 3}
          />
          <div className="space-y-2 border-2 border-foreground bg-card p-3 shadow-neo-sm">
            <p className="text-[10px] font-mono font-black uppercase tracking-widest text-foreground/70">
              Buffer fill
            </p>
            <p className={cn(
              'font-mono text-base font-black',
              stats.bufferFillPercent < 20
                ? 'text-destructive'
                : stats.bufferFillPercent < 60
                  ? 'text-accent'
                  : 'text-foreground'
            )}>
              {stats.bufferFillPercent}%
            </p>
            <div className="h-3 border-2 border-foreground bg-background">
              <div
                className={cn(
                  'h-full transition-all duration-500',
                  stats.bufferFillPercent < 20 ? 'bg-destructive' : 'bg-accent'
                )}
                style={{ width: `${stats.bufferFillPercent}%` }}
              />
            </div>
          </div>
          <MetricCard
            label="Segment latency"
            value={formatLatency(stats.segmentLatency)}
            sub={`${stats.switchCount} switches`}
          />
        </div>

        <Tabs defaultValue="bandwidth">
          <TabsList className="grid h-auto w-full grid-cols-2 border-2 border-foreground bg-background p-1 shadow-neo-sm">
            <TabsTrigger value="bandwidth" className="text-xs font-black data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
              Bandwidth
            </TabsTrigger>
            <TabsTrigger value="buffer" className="text-xs font-black data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
              Buffer
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bandwidth" className="mt-3 space-y-2">
            <p className="text-[10px] font-black uppercase tracking-widest text-foreground/70">
              Bandwidth + level bitrate (kbps)
            </p>
            <div className="overflow-x-auto border-2 border-foreground bg-background p-2 shadow-neo-sm sm:p-3">
              <ResponsiveContainer width="100%" height={150}>
                <LineChart data={bandwidthHistory} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted-foreground))" opacity={0.25} />
                  <XAxis dataKey="index" hide />
                  <YAxis tick={{ fill: 'hsl(var(--foreground))', fontSize: 10 }} width={46} />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    formatter={(value: number, name: string) => [
                      `${value} kbps`,
                      name === 'bandwidth' ? 'Bandwidth' : 'Level bitrate',
                    ]}
                    labelFormatter={() => ''}
                  />
                  {levels.map((lvl) => (
                    <ReferenceLine
                      key={lvl.height}
                      y={Math.round(lvl.bitrate / 1000)}
                      stroke="hsl(var(--foreground))"
                      strokeDasharray="4 3"
                      strokeOpacity={0.3}
                      strokeWidth={1}
                    />
                  ))}
                  <Line type="monotone" dataKey="bandwidth" stroke="hsl(var(--accent))" strokeWidth={3} dot={false} isAnimationActive={false} />
                  <Line type="stepAfter" dataKey="instantBitrate" stroke="hsl(var(--foreground))" strokeWidth={2} dot={false} strokeDasharray="6 4" isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-3 text-[10px] font-bold text-foreground/75">
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-1 w-4 bg-accent" />
                EWMA
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-4 border-t-2 border-dashed border-foreground" />
                Level bitrate
              </span>
              {levels.map((lvl) => (
                <span key={lvl.height}>{lvl.height}p</span>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="buffer" className="mt-3 space-y-2">
            <p className="text-[10px] font-black uppercase tracking-widest text-foreground/70">
              Buffer seconds
            </p>
            <div className="overflow-x-auto border-2 border-foreground bg-background p-2 shadow-neo-sm sm:p-3">
              <ResponsiveContainer width="100%" height={150}>
                <LineChart data={bandwidthHistory} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted-foreground))" opacity={0.25} />
                  <XAxis dataKey="index" hide />
                  <YAxis tick={{ fill: 'hsl(var(--foreground))', fontSize: 10 }} domain={[0, MAX_BUFFER_DISPLAY]} width={34} />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    formatter={(value: number) => [`${value} s`, 'Buffer']}
                    labelFormatter={() => ''}
                  />
                  <ReferenceLine y={3} stroke="hsl(var(--destructive))" strokeDasharray="4 3" strokeWidth={1} />
                  <ReferenceLine y={10} stroke="hsl(var(--accent))" strokeDasharray="4 3" strokeWidth={1} />
                  <Line type="monotone" dataKey="bufferLength" stroke="hsl(var(--accent))" strokeWidth={3} dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </TabsContent>
        </Tabs>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[10px] font-black uppercase tracking-widest text-foreground/70">
              Switch log
            </p>
            <span className="text-[10px] font-bold text-foreground/70">
              {switchLog.length} events
            </span>
          </div>
          <div className="max-h-36 overflow-auto border-2 border-foreground bg-background shadow-neo-sm">
            {switchLog.length === 0 ? (
              <p className="p-4 text-center text-xs font-bold text-foreground/60">
                No quality switches yet.
              </p>
            ) : (
              <table className="w-full min-w-[420px] text-xs">
                <thead className="sticky top-0 bg-accent text-accent-foreground">
                  <tr className="border-b-2 border-foreground">
                    <th className="px-2 py-2 text-left font-black uppercase">Time</th>
                    <th className="px-2 py-2 text-left font-black uppercase">From</th>
                    <th className="px-2 py-2 text-left font-black uppercase">To</th>
                    <th className="px-2 py-2 text-right font-black uppercase">BW</th>
                  </tr>
                </thead>
                <tbody>
                  {[...switchLog].reverse().map((ev, index) => (
                    <tr key={`${ev.time}-${index}`} className="border-b border-foreground/25 last:border-0">
                      <td className="px-2 py-2 font-bold text-foreground/75">{formatTime(ev.time)}</td>
                      <td className="px-2 py-2"><QualityBadge quality={ev.from} /></td>
                      <td className="px-2 py-2">
                        <span className="flex items-center gap-1.5">
                          <span className={ev.direction === 'up' ? 'text-accent' : 'text-destructive'}>
                            {ev.direction === 'up' ? 'up' : 'down'}
                          </span>
                          <QualityBadge quality={ev.to} />
                        </span>
                      </td>
                      <td className="px-2 py-2 text-right font-black text-foreground">
                        {ev.bandwidth}k
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <p className="border-t-2 border-foreground pt-3 text-center text-[11px] font-bold text-foreground/70">
          {ABR_ALGO_INFO[abrAlgorithm].short}: {formatBandwidth(stats.bandwidth)} | Latency: {formatLatency(stats.segmentLatency)} | Switches: {stats.switchCount}
        </p>
      </div>
    </div>
  );
};
